/**
 * @file ClientList.tsx
 * @description Component for displaying and managing a list of clients.
 */

import React, { useState } from "react";
import { Client } from "../../types";
import { useClients } from "../../hooks/useClients";
import ClientForm from "./ClientForm";
import Loading from "../common/Loading";

interface ClientListProps {
  onClientCreated?: (clientId: string) => void;
}

const ClientList: React.FC<ClientListProps> = ({ onClientCreated }) => {
  const { clients, isLoading, createClient, updateClient, deleteClient } =
    useClients();
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleCreate = () => {
    setEditingClient(null);
    setIsFormOpen(true);
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setIsFormOpen(true);
  };

  const handleDelete = async (clientId: string) => {
    if (
      window.confirm(
        "Sei sicuro di voler eliminare questo cliente? I siti web associati non verranno eliminati, ma non avranno più un cliente associato."
      )
    ) {
      try {
        await deleteClient(clientId);
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingClient(null);
  };

  const handleFormSubmit = async (name: string) => {
    try {
      if (editingClient) {
        await updateClient(editingClient.id, name);
      } else {
        const clientId = await createClient(name);
        // Notify parent component about the newly created client
        if (onClientCreated && clientId) {
          onClientCreated(clientId);
        }
      }
      handleFormClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (isLoading && clients.length === 0) {
    return <Loading />;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="!text-3xl !font-medium text-primary">Clienti</h3>
        <button
          onClick={handleCreate}
          className="px-6 text-base rounded-full btn btn-primary"
          disabled={isFormOpen}
        >
          + Nuovo cliente
        </button>
      </div>

      {/* Show form at top only when creating a new client */}
      {isFormOpen && !editingClient && (
        <ClientForm
          client={null}
          onSubmit={handleFormSubmit}
          onCancel={handleFormClose}
        />
      )}

      {clients.length === 0 ? (
        <p className="text-gray">Nessun cliente ancora. Crea il primo!</p>
      ) : (
        <div className="flex flex-col gap-4">
          {clients.map((client) => (
            <div key={client.id}>
              {editingClient && editingClient.id === client.id ? (
                // In-place editing: show form instead of the row
                <ClientForm
                  client={editingClient}
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormClose}
                  isInline={true}
                />
              ) : (
                // Normal row display
                <div className="flex items-center justify-between p-4 border rounded-lg border-gray-line bg-base-100">
                  <span className="text-lg">{client.name}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(client)}
                      className="px-4 text-sm rounded-full btn btn-outline btn-primary"
                      disabled={isFormOpen}
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="px-4 text-sm rounded-full btn btn-outline btn-error"
                      disabled={isFormOpen}
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientList;
